import { Link } from "react-router-dom";
import { ArrowRight, Mail } from "lucide-react";
import { Logo } from "./Logo";

const EXPLORE = [
  { label: "Discover", to: "/#features", testId: "footer-link-features" },
  { label: "How It Works", to: "/#how-it-works", testId: "footer-link-how-it-works" },
  { label: "Achievements", to: "/#achievements", testId: "footer-link-achievements" },
  { label: "Screenshots", to: "/#screenshots", testId: "footer-link-screenshots" },
  { label: "Membership & Pricing", to: "/#pricing", testId: "footer-link-pricing" },
  { label: "Compare", to: "/compare", testId: "footer-link-compare" },
];

const RESOURCES = [
  { label: "About Us", to: "/about", testId: "footer-link-about" },
  { label: "Blog", to: "/blog", testId: "footer-link-blog" },
  { label: "Tutorials", to: "/tutorials", testId: "footer-link-tutorials" },
];

const LEGAL = [
  { label: "Privacy Policy", to: "/privacy", testId: "footer-privacy-link" },
  { label: "Terms of Service", to: "/terms", testId: "footer-terms-link" },
  { label: "Data Deletion", to: "/data-deletion", testId: "footer-data-deletion-link" },
  { label: "Location Data", to: "/location", testId: "footer-location-link" },
  { label: "Third-Party Services", to: "/third-party", testId: "footer-third-party-link" },
];

const SUPPORT_FORM_URL =
  "https://forms.zohopublic.com/foodiecrave4ugm1/form/CustomerSupportForm/formperma/RzlQJWKo2FHdmJj54ES7g4Hpv0gkn1cX5RIONNP7omw";

const linkClass = "text-sm text-crave-muted transition-colors duration-300 hover:text-crave-cream";

export const Footer = () => (
  <footer data-testid="footer" className="border-t border-crave-border bg-[#100e0d]">
    <div className="mx-auto max-w-7xl px-6 py-16 md:px-12">
      <div className="grid grid-cols-1 gap-12 sm:grid-cols-2 lg:grid-cols-4">
        <div>
          <Logo testId="footer-logo" />
          <p className="mt-5 max-w-sm font-display text-base italic text-crave-cream/90">
            Seek out new spots locally or at your next destination. Remember what you love. Enjoy the experience.
          </p>
          <p className="mt-3 max-w-sm text-sm leading-relaxed text-crave-muted">
            Foodie Crave is a dining experience app with restaurant discovery built in. Explore
            places to eat, save favorites, and remember your dining adventures.
          </p>
          <Link
            to="/about"
            data-testid="footer-our-story-button"
            className="mt-6 inline-flex items-center gap-2 rounded-full border border-crave-gold/40 px-5 py-2.5 text-sm font-semibold text-crave-gold transition-colors duration-300 hover:bg-crave-gold hover:text-crave-bg"
          >
            Our Story
            <ArrowRight className="h-4 w-4" aria-hidden="true" />
          </Link>
          <a
            href="mailto:support@foodiecrave.app"
            data-testid="footer-support-email"
            className="mt-6 flex items-center gap-2 text-sm font-medium text-crave-gold transition-colors duration-300 hover:text-crave-cream"
          >
            <Mail className="h-4 w-4" aria-hidden="true" />
            Contact: support@foodiecrave.app
          </a>
          <p data-testid="footer-founder-note" className="mt-6 max-w-sm text-xs leading-relaxed text-crave-muted">
            Created by <span className="font-semibold text-crave-cream">Knight Rose</span> — a
            husband-and-wife team who believe dining out should be about making memories, not
            endless scrolling.
          </p>
        </div>

        <nav aria-label="Explore">
          <h3 className="text-xs font-bold uppercase tracking-[0.22em] text-crave-cream">Explore</h3>
          <ul className="mt-5 space-y-3">
            {EXPLORE.map((link) => (
              <li key={link.to}>
                <Link to={link.to} data-testid={link.testId} className={linkClass}>
                  {link.label}
                </Link>
              </li>
            ))}
          </ul>
        </nav>

        <nav aria-label="About and resources">
          <h3 className="text-xs font-bold uppercase tracking-[0.22em] text-crave-cream">
            About &amp; Resources
          </h3>
          <ul className="mt-5 space-y-3">
            {RESOURCES.map((link) => (
              <li key={link.to}>
                <Link to={link.to} data-testid={link.testId} className={linkClass}>
                  {link.label}
                </Link>
              </li>
            ))}
            <li>
              <a
                href={SUPPORT_FORM_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-support-form-link"
                className={linkClass}
              >
                Customer Support
              </a>
            </li>
          </ul>
        </nav>

        <nav aria-label="Legal">
          <h3 className="text-xs font-bold uppercase tracking-[0.22em] text-crave-cream">Legal</h3>
          <ul className="mt-5 space-y-3">
            {LEGAL.map((link) => (
              <li key={link.to}>
                <Link to={link.to} data-testid={link.testId} className={linkClass}>
                  {link.label}
                </Link>
              </li>
            ))}
          </ul>
        </nav>
      </div>

      <div className="mt-14 flex flex-col items-start justify-between gap-4 border-t border-crave-border pt-8 sm:flex-row sm:items-center">
        <p className="text-xs text-crave-muted">© 2026 Foodie Crave. All rights reserved.</p>
        <p className="text-xs text-crave-muted">Made for people who love a good meal.</p>
      </div>
    </div>
  </footer>
);
